import React, { useEffect, useState } from 'react';
import Button from '@/common/components/ui/Button';
import UtilityService from '@/services/util/utility-service';

interface Props {
  fileBlob?: Blob | null;
  mimeType?: string;
  emptyMessage?: string;
  fileName?: string;
}

const EmbeddedFileViewer = ({
  fileBlob,
  mimeType,
  emptyMessage = 'No file available for preview',
  fileName,
}: Props) => {
  const [blobURL, setBlobURL] = useState<string | undefined>();
  const [resetViewer, setResetViewer] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const handleDownloadFile = () => {
    if (!fileBlob) return;
    UtilityService.downloadBlob(fileBlob, fileName || 'file');
  };

  useEffect(() => {
    const handleInternalError = (e: MessageEvent) => {
      if (e.data && typeof e.data === 'string' && e.data.includes('pdfjs')) {
        console.error('Internal PDF Viewer Error:', e.data);
        setHasError(true);
      }
    };

    window.addEventListener('message', handleInternalError);
    return () => window.removeEventListener('message', handleInternalError);
  }, []);

  useEffect(() => {
    let url: string | undefined;

    if (fileBlob) {
      if (
        mimeType == 'application/pdf' ||
        mimeType == 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mimeType == 'application/msword' ||
        mimeType == 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        mimeType == 'application/vnd.ms-powerpoint'
      ) {
        loadPdf(fileBlob);

        setResetViewer(true);
      } else {
        setBlobURL('Invalid type');
      }
    }

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [fileBlob, mimeType]);

  useEffect(() => {
    if (resetViewer) {
      setTimeout(() => setResetViewer(false), 0);
    }
  }, [resetViewer]);

  if (resetViewer) {
    return <div>Loading...</div>;
  }

  if (!fileBlob) {
    return (
      <div className='flex items-center justify-center h-full text-text_gray_700'>
        {emptyMessage}
      </div>
    );
  }

  const loadPdf = async (blob: Blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const uint8 = new Uint8Array(arrayBuffer);

      const header = Array.from(uint8.slice(0, 5))
        .map((byte) => String.fromCharCode(byte))
        .join('');
      if (header !== '%PDF-') {
        throw new Error('Invalid PDF header');
      }

      setBlobURL(URL.createObjectURL(blob));
    } catch (err) {
      setHasError(true);
      console.error('Failed to load PDF:', err);
    }
  };

  if (hasError) {
    return (
      <div className='flex items-center justify-center h-full text-text_gray_700'>
        PDF is corrupted or file is not a valid PDF.
      </div>
    );
  }

  return blobURL ? (
    <>
      {mimeType == 'application/pdf' && (
        <object data={blobURL} type={mimeType ?? 'application/pdf'} width='100%' height='100%' />
      )}
      {(mimeType == 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mimeType == 'application/msword' ||
        mimeType == 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        mimeType == 'application/vnd.ms-powerpoint') && (
        <div className='flex flex-col items-center justify-center h-full text-text_gray_700 gap-4'>
          <p>Document preview not available. Use download button below.</p>
          <Button
            onClick={handleDownloadFile}
            className='bg-blue-600 text-white rounded-lg px-4 py-2 flex items-center justify-center gap-2 hover:bg-blue-700'
          >
            Download File
          </Button>
        </div>
      )}
      {blobURL == 'Invalid type' && (
        <div className='flex items-center justify-center h-full text-text_gray_700'>
          <p>Something went wrong. Invalid document found.</p>
        </div>
      )}
    </>
  ) : (
    <div className='flex items-center justify-center h-full'>Loading...</div>
  );
};

export default EmbeddedFileViewer;
